import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Key, Link as LinkIcon, Building2, Copy, Check, Eye, EyeOff,
  RefreshCw, Save, AlertTriangle, Percent, Info, Shield, Zap, ChevronRight,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/shared/components/ui/use-toast";
import * as merchantService from "@/shared/api/services/merchantService";

/* ─────────────── GLOBAL STYLES ─────────────── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');

.ms-root {
  font-family: 'DM Sans', sans-serif;
  padding-bottom: 60px;
  --cyan: #22d3ee;
  --cyan-dim: rgba(34,211,238,0.12);
  --cyan-glow: rgba(34,211,238,0.25);
  --surface: rgba(8,16,38,0.96);
  --surface-2: rgba(11,22,50,0.95);
  --border: rgba(34,211,238,0.1);
  --border-hover: rgba(34,211,238,0.3);
   --text-1: #cce6ff;   /* primary text, bright on dark navy */
  --text-2: #88bcd6;   /* secondary text */
  --text-3: #5a7d99;   /* muted text *
}
.ms-root * { box-sizing: border-box; }

/* ── Section header ── */
.ms-page-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.9rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: #eaf6ff;
}
.ms-page-sub {
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: #9ccfe8;
}

/* ── Card ── */
.ms-card {
  background: linear-gradient(145deg, var(--surface), var(--surface-2));
  border: 1px solid var(--border);
  border-radius: 22px;
  padding: 26px 28px;
  position: relative; overflow: hidden;
  transition: border-color 0.35s ease, box-shadow 0.35s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
  will-change: transform;
  
}
.ms-card::before {
  content: '';
  position: absolute; top: 0; left: 20%; right: 20%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.4), transparent);
}
.ms-card::after {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none;
  background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(34,211,238,0.04), transparent);
}
.ms-card:hover {
  border-color: rgba(34,211,238,0.22);
  box-shadow: 0 0 60px rgba(34,211,238,0.07), 0 24px 60px rgba(0,0,0,0.35);
  transform: translateY(-2px);
}

/* ── Card header ── */
.ms-card-header {
  display: flex; align-items: center; gap: 14px; margin-bottom: 22px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(34,211,238,0.07);
}
.ms-icon-box {
  width: 46px; height: 46px; border-radius: 15px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
}
.ms-icon-box::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(145deg, rgba(255,255,255,0.15), transparent);
  border-radius: inherit;
}
.ms-card-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #eaf6ff;
}
.ms-card-title { color: var(--text-1); }
.ms-card-desc {
  font-family: 'DM Sans', sans-serif;
  font-size: 14.5px;
  font-weight: 500;
  letter-spacing: -0.005em;
  color: #8bbbd3;
}

/* ── Label ── */
.ms-label {
  display: block; margin-bottom: 7px;
  color: var(--text-3); font-size: 10.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.1em;
  
}
  .ms-label { color: var(--text-3); }
  

/* ── Key display ── */
.ms-key-row {
  display: flex; gap: 10px; align-items: stretch;
}
.ms-key-box {
  flex: 1;
  background: rgba(34,211,238,0.03);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 13px; padding: 13px 18px;
  display: flex; align-items: center; justify-content: space-between;
  transition: border-color 0.25s, background 0.25s;
  min-width: 0;
}
.ms-key-box:hover {
  border-color: rgba(34,211,238,0.2);
  background: rgba(34,211,238,0.05);
}
.ms-key-text {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12.5px; font-weight: 500; color: #7aaec8;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ms-eye-btn {
  background: none; border: none; cursor: pointer;
  color: var(--text-3); padding: 2px; margin-left: 8px; flex-shrink: 0;
  transition: color 0.2s, transform 0.2s;
}
.ms-eye-btn:hover { color: var(--cyan); transform: scale(1.15); }

.ms-icon-btn {
  width: 44px; height: 44px; border-radius: 13px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(34,211,238,0.06);
  border: 1px solid rgba(34,211,238,0.15);
  cursor: pointer; color: var(--text-3);
  transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
}
.ms-icon-btn:hover {
  color: var(--cyan); border-color: rgba(34,211,238,0.4);
  background: rgba(34,211,238,0.12);
  box-shadow: 0 0 20px rgba(34,211,238,0.2);
  transform: scale(1.08);
}
.ms-icon-btn:active { transform: scale(0.95); }

/* ── Input ── */
.ms-input {
  width: 100%;
  background: rgba(34,211,238,0.03);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 13px; padding: 12px 16px;
  color: var(--text-1); font-size: 14px; font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  outline: none;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}
.ms-input::placeholder { color: var(--text-3); }
..ms-input:focus {
  border-color: rgba(34,211,238,0.4);
  background: rgba(34,211,238,0.06);
  box-shadow: 0 0 0 4px rgba(34,211,238,0.07), 0 8px 30px rgba(0,0,0,0.4);
}
.ms-input[type="number"]::-webkit-outer-spin-button,
.ms-input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.ms-input[type="number"] {
  -moz-appearance: textfield;
}
.ms-input:disabled {
  opacity: 0.55; cursor: not-allowed;
  background: rgba(255,255,255,0.02);
}
.ms-hint { color: var(--text-3); font-size: 11.5px; margin-top: 6px; font-weight: 500; }

/* ── Divider ── */
.ms-divider {
  border: none; border-top: 1px solid rgba(34,211,238,0.07);
  margin: 22px 0;
}

/* ── Primary button ── */
.ms-btn-primary {
  display: inline-flex; align-items: center; gap: 9px;
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  border: none; border-radius: 13px; padding: 12px 18px;
  min-width: 148px; max-width: 260px; width: fit-content;
  color: #fff; font-size: 13.5px; font-weight: 700;
  font-family: 'DM Sans', sans-serif; cursor: pointer;
  position: relative; overflow: hidden;
  transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s, opacity 0.2s;
  box-shadow: 0 8px 28px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.15);
}
.ms-btn-primary::before {
  content: ''; position: absolute;
  top: 0; left: -100%; width: 55%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.13), transparent);
  transform: skewX(-15deg); transition: left 0.55s;
}
.ms-btn-primary:hover:not(:disabled)::before { left: 160%; }
.ms-btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 14px 38px rgba(6,182,212,0.5);
}
.ms-btn-primary:active:not(:disabled) { transform: translateY(0) scale(0.97); }
.ms-btn-primary:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── Danger button ── */
.ms-btn-danger {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(245,158,11,0.08);
  border: 1px solid rgba(245,158,11,0.25);
  border-radius: 12px; padding: 10px 18px;
  color: #fbbf24; font-size: 13px; font-weight: 700;
  font-family: 'DM Sans', sans-serif; cursor: pointer;
  transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
}
.ms-btn-danger:hover {
  background: rgba(245,158,11,0.14);
  border-color: rgba(245,158,11,0.45);
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(245,158,11,0.2);
}

/* ── Metric box ── */
.ms-metric-box {
  background: rgba(34,211,238,0.04);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 14px; padding: 16px 18px;
  transition: border-color 0.25s, background 0.25s, transform 0.25s cubic-bezier(0.34,1.56,0.64,1);
  cursor: default;
}
.ms-metric-box:hover {
  border-color: rgba(34,211,238,0.22);
  background: rgba(34,211,238,0.07);
  transform: translateY(-2px);
}
.ms-metric-label { color: var(--text-3); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.09em; margin-bottom: 6px; }
.ms-metric-val {
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.45rem; font-weight: 700; color: var(--text-1);
  letter-spacing: -0.02em;
}

/* ── Parent info box ── */
.ms-parent-box {
  background: rgba(14,165,233,0.06);
  border: 1px solid rgba(14,165,233,0.18);
  border-radius: 16px; padding: 18px 20px;
  position: relative; overflow: hidden;
}
.ms-parent-box::before {
  content: '';
  position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(14,165,233,0.35), transparent);
}

/* ── Spinner ── */
@keyframes spin { to { transform: rotate(360deg); } }
.ms-spin { animation: spin 0.85s linear infinite; }

/* ── Responsive ── */
@media (max-width: 640px) {
  .ms-page-title { font-size: 1.55rem; }
  .ms-card { padding: 18px 16px; border-radius: 18px; }
  .ms-metrics-grid { grid-template-columns: 1fr !important; }
  .ms-key-text { font-size: 11px; }
}
`;

/* ─────────────── CARD WRAPPER ─────────────── */
const SectionCard = ({ children, delay = 0 }) => (
  <motion.div
    className="ms-card"
    initial={{ opacity: 0, y: 28, filter: "blur(6px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

/* ─────────────── MAIN PAGE ─────────────── */
export default function MerchantSettings() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const { data: merchantProfile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: () => merchantService.getProfile(),
  });

  const [secretKey, setSecretKey] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [callbackUrl, setCallbackUrl] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState({});

  useEffect(() => {
    if (!merchantProfile) return;

    setCallbackUrl(merchantProfile.callbackUrl || "");
    setWebhookUrl(merchantProfile.webhookUrl || "");
    setSecretKey(merchantProfile.secretKey || "");
    setApiKey(merchantProfile.apiKey || "");

    setProfile({
      businessName: merchantProfile.contactName || "",
      email: merchantProfile.contactEmail || "",
      supportContact: merchantProfile.contactPhone || "",
    });
  }, [merchantProfile]);

  const copyToClipboard = (text, keyType) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyType);
    setTimeout(() => setCopiedKey(null), 2200);
  };

  const maskKey = (key) =>
    key ? key.slice(0, 12) + "••••••••••••••••" + key.slice(-4) : "";

  const generateRandomKey = (length = 32) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      result += chars[randomValues[i] % chars.length];
    }

    return result;
  };

  const generateApiKeys = () => {
    return {
      apiKey: `pk_live_${generateRandomKey(32)}`,
      secretKey: `sk_live_${generateRandomKey(40)}`,
    };
  };

  const handleRegenerateKeys = async () => {
    setSaving(true);
    const keys = generateApiKeys();
    setApiKey(keys.apiKey);
    setSecretKey(keys.secretKey);
    setShowSecret(true);

    try {
      await merchantService.updateProfile({ apiKey: keys.apiKey, secretKey: keys.secretKey });
      queryClient.invalidateQueries(["merchant-profile"]);
      toast({ title: t("success") || "Success", description: t("apiKeysRegenerated") || "API keys regenerated" });
    } catch (error) {
      toast({ variant: "destructive", title: t("error") || "Error", description: error?.message || t("failedToRegenerateApiKeys") || "Failed to regenerate API keys" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUrls = async () => {
    setSaving(true);
    try {
      await merchantService.updateProfile({ callbackUrl, webhookUrl });
      queryClient.invalidateQueries(["merchant-profile"]);
      toast({ title: t("success") || "Success", description: t("callbackWebhookUpdated") || "Callback & webhook URL saved" });
    } catch (error) {
      toast({ variant: "destructive", title: t("error") || "Error", description: error?.message || t("failedToSave") || "Failed to save callback/webhook URLs" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    const email = profile.email?.trim();
    const phone = profile.supportContact?.trim();

    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      toast({ variant: "destructive", title: t("invalid Email") || "Invalid email", description: "Please Enter Valid Email" || "Please enter a valid email address" });
      return;
    }

    if (phone && !/^[0-9]+$/.test(phone)) {
      toast({ variant: "destructive", title: t("invalidPhone") || "Invalid phone", description: "Please Enter Valid Phone" || "Please enter a valid numeric phone number" });
      return;
    }

    setSavingProfile(true);
    try {
      await merchantService.updateProfile({
        contactName: profile.businessName || "",
        contactEmail: email || "",
        contactPhone: phone || "",
      });
      queryClient.invalidateQueries(["merchant-profile"]);
      toast({ title: t("success") || "Success", description: t("profileUpdated") || "Profile updated" });
    } catch (error) {
      toast({ variant: "destructive", title: t("error") || "Error", description: error?.message || t("failedToSave") || "Failed to save profile" });
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <MerchantLayout
      currentPage="MerchantSettings"
      merchantName={merchantProfile?.name || ''}
      merchantCode={merchantProfile?.code || ''}
    >
      <style>{STYLE}</style>

      <div className="ms-root">
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 22 }}>

          {/* ── Page Header ── */}
          <motion.div
            initial={{ opacity: 0, y: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="ms-page-title">{t("settings")}</h1>
            <p className="ms-page-sub">{t("manageProfile")}</p>
          </motion.div>

          {/* ── Business Profile ── */}
          <SectionCard delay={0.05}>
            <div className="ms-card-header">
              <div className="ms-icon-box" style={{ background: "linear-gradient(135deg, #10b981, #059669)", boxShadow: "0 8px 24px rgba(16,185,129,0.4)" }}>
                <Building2 size={20} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
              </div>
              <div>
                <div className="ms-card-title">{t("merchantProfile")}</div>
                <div className="ms-card-desc">{t("yourBusinessInfo")}</div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { id: "businessName", label: t("businessName"), type: "text", key: "businessName", placeholder: "Your company name" },
                { id: "email", label: t("emailAddress"), type: "email", key: "email", placeholder: "you@company.com" },
                { id: "support", label: t("supportContact"), type: "number", key: "supportContact", placeholder: "+66 XXX XXX XXXX" },
              ].map(({ id, label, type, key, placeholder }) => (
                <div key={id}>
                  <label className="ms-label" htmlFor={id}>{label}</label>
                  <input
                    className="ms-input" id={id} type={type}
                    value={profile[key]}
                    onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
                    placeholder={placeholder}
                  />
                </div>
              ))}

              <motion.button
                className="ms-btn-primary"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                whileTap={!savingProfile ? { scale: 0.97 } : {}}
              >
                {savingProfile
                  ? <div className="ms-spin"><RefreshCw size={15} /></div>
                  : <Save size={15} />
                }
                {t("saveProfile")}
              </motion.button>
            </div>
          </SectionCard>

          {/* ── API Keys ── */}
          <SectionCard delay={0.05}>
            <div className="ms-card-header">
              <div className="ms-icon-box bg-gradient-to-br from-cyan-500 to-cyan-700 shadow-[0_8px_24px_rgba(6,182,212,0.4)]">
                <Key size={20} className="text-white relative z-10" />
              </div>

              <div>
                <div className="ms-card-title">{t("apiKeys")}</div>
                <div className="ms-card-desc">{t("yourAuthCredentials")}</div>
              </div>
            </div>

            {/* Public Key */}
            <div className="mb-[18px]">
              <label className="ms-label">{t("apiKeyPublic")}</label>

              <div className="ms-key-row">
                <div className="ms-key-box">
                  <code className="ms-key-text">{apiKey}</code>
                </div>

                <motion.button
                  className="ms-icon-btn"
                  onClick={() => copyToClipboard(apiKey, "api")}
                  whileTap={{ scale: 0.9 }}
                >
                  <AnimatePresence mode="wait">
                    {copiedKey === "api" ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0, rotate: -90 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                      >
                        <Check size={16} className="text-emerald-500" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="copy"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Copy size={16} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>

            {/* Secret Key */}
            <div>
              <label className="ms-label">{t("secretKey")}</label>

              <div className="ms-key-row">
                <div className="ms-key-box">
                  <code className="ms-key-text">
                    {showSecret ? secretKey : maskKey(secretKey)}
                  </code>

                  <motion.button
                    className="ms-eye-btn"
                    onClick={() => setShowSecret(!showSecret)}
                    whileTap={{ scale: 0.9 }}
                  >
                    <AnimatePresence mode="wait">
                      {showSecret ? (
                        <motion.div
                          key="off"
                          initial={{ opacity: 0, scale: 0.7 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.7 }}
                        >
                          <EyeOff size={15} />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="on"
                          initial={{ opacity: 0, scale: 0.7 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.7 }}
                        >
                          <Eye size={15} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </div>

                <motion.button
                  className="ms-icon-btn"
                  onClick={() => copyToClipboard(secretKey, "secret")}
                  whileTap={{ scale: 0.9 }}
                >
                  <AnimatePresence mode="wait">
                    {copiedKey === "secret" ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0, rotate: -90 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                      >
                        <Check size={16} className="text-emerald-500" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="copy"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Copy size={16} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>

            <hr className="ms-divider" />

            {/* Regenerate */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-[13.5px] font-semibold text-[var(--text-1)]">
                  {t("regenerateKeys")}
                </p>
                <p className="text-xs text-[var(--text-3)] mt-0.5">
                  {t("invalidateCurrentKeys")}
                </p>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <motion.button className="ms-btn-danger" whileTap={{ scale: 0.96 }}>
                    <RefreshCw size={14} />
                    {t("regenerate")}
                  </motion.button>
                </AlertDialogTrigger>

                <AlertDialogContent className="bg-[rgba(8,16,38,0.98)] border border-cyan-400/20 rounded-[20px] backdrop-blur-xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-[#e8f4ff] font-['Syne']">
                      <AlertTriangle size={20} className="text-amber-500" />
                      {t("regenerateApiKeys")}
                    </AlertDialogTitle>

                    <AlertDialogDescription className="text-[#3a5a78]">
                      {t("invalidateImmediately")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-cyan-400/5 border border-cyan-400/20 text-[#7aaec8] rounded-xl">
                      {t("cancel")}
                    </AlertDialogCancel>

                    <AlertDialogAction
                      onClick={handleRegenerateKeys}
                      className="bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl border-none"
                    >
                      {t("regenerateKeys")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </SectionCard>

          {/* ── Callback URLs ── */}
          <SectionCard delay={0.1}>
            <div className="ms-card-header">
              <div className="ms-icon-box" style={{ background: "linear-gradient(135deg, #7c3aed, #6d28d9)", boxShadow: "0 8px 24px rgba(124,58,237,0.4)" }}>
                <LinkIcon size={20} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
              </div>
              <div>
                <div className="ms-card-title">{t("callbackUrls")}</div>
                <div className="ms-card-desc">{t("whereWeSendNotifications")}</div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label className="ms-label" htmlFor="callback">{t("callbackUrl")}</label>
                <input className="ms-input" id="callback" value={callbackUrl} onChange={(e) => setCallbackUrl(e.target.value)} placeholder="https://your-site.com/api/callback" />
                <p className="ms-hint">{t("wePostToEndpoint")}</p>
              </div>

              <div>
                <label className="ms-label" htmlFor="webhook">{t("webhookUrl")}</label>
                <input className="ms-input" id="webhook" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://your-site.com/api/webhook" />
                <p className="ms-hint">{t("additionalEventNotifications")}</p>
              </div>

              <motion.button
                className="ms-btn-primary"
                onClick={handleSaveUrls}
                disabled={saving}
                whileTap={!saving ? { scale: 0.97 } : {}}
              >
                {saving
                  ? <div className="ms-spin"><RefreshCw size={15} /></div>
                  : <Save size={15} />
                }
                {t("saveUrls")}
              </motion.button>
            </div>
          </SectionCard>

          {/* ── Fees & Commission ── */}
          {merchantProfile && (
            <SectionCard delay={0.15}>
              <div className="ms-card-header">
                <div className="ms-icon-box" style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)", boxShadow: "0 8px 24px rgba(245,158,11,0.4)" }}>
                  <Percent size={20} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
                </div>
                <div>
                  <div className="ms-card-title">{t("feesAndCommission")}</div>
                  <div className="ms-card-desc">{t("commissionRatesAndFeeInfo")}</div>
                </div>
              </div>

              {/* Parent info */}
              {merchantProfile.parentName && (
                <motion.div
                  className="ms-parent-box"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  style={{ marginBottom: 20 }}
                >
                  <div style={{ display: "flex", gap: 12 }}>
                    <Info size={16} style={{ color: "#38bdf8", flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <h4 style={{ color: "#7dd3fc", fontSize: 12, fontWeight: 700, marginBottom: 14 }}>
                        {t("givenBy")} {merchantProfile.parentType}: {merchantProfile.parentName}
                      </h4>
                      <div className="ms-metrics-grid !grid-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                        {[
                          { label: t("deposit Fee"), val: merchantProfile.parentDepositFee },
                          { label: t("withdrawal Fee"), val: merchantProfile.parentWithdrawalFee },
                        ].map(({ label, val }) => (
                          <div key={label} className="ms-metric-box">
                            <div className="ms-metric-label">{label}</div>
                            <div className="ms-metric-val" style={{ fontSize: "1.25rem", color: "#38bdf8" }}>
                              {val != null ? `${val}%` : "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Your rates */}
              <div>
                <label className="ms-label" style={{ marginBottom: 12 }}>{t("your fee percentages")}</label>
                <div className="ms-metrics-grid !grid-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                  {[
                    { label: `${t("deposit Fee")} (%)`, val: merchantProfile.depositFee },
                    { label: `${t("withdrawal Fee")} (%)`, val: merchantProfile.withdrawalFee },
                  ].map(({ label, val }) => (
                    <div key={label} className="ms-metric-box">
                      <div className="ms-metric-label">{label}</div>
                      <div className="ms-metric-val">
                        {val != null ? `${val}%` : t("nA")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          )}

        </div>
      </div>
    </MerchantLayout>
  );
}